import React from 'react';
import { withRouter } from 'react-router-dom';

// material components
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import StyledBadge from '@material-ui/core/Badge';
import IconButton from '@material-ui/core/IconButton';

// custom components
import AlertsDrawer from './AlertsDrawer';
import withShipment from '../../withShipment';

// constants

// redux
import { alertEntriesSelector } from '../../redux/selectors/alerts';
import { userInfoSelector } from '../../redux/selectors/auth';
import {
  fetchAlerts,
  snoozeAlert,
  resolveAlert,
  deleteAllAlerts,
} from '../../redux/actions/alerts';
import PropTypes from 'prop-types';

import { tableSettingSelector } from '../../redux/selectors/account';
import { getTableSetting } from '../../redux/actions/account';
import { Tooltip } from '@mui/material';

// styling

class Alerts extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      alertDrawerOpen: false,
      alerts: [],
    };
  }

  handleAlertDrawerClose = () => {
    this.setState({
      alertDrawerOpen: false,
    });
  };

  parseAlertTimestamp = (ts) => {
    if (!ts) return 0;
    // Format: "MM\dd\yyyy h:mm a" or "MM/dd/yyyy h:mm a"
    const parts = ts.trim().split(/\s+/);
    const datePart = parts[0];
    const timePart = parts[1] || '00:00';
    const meridiem = (parts[2] || 'AM').toUpperCase();

    const [mm, dd, yyyy] = datePart.split(/[/\\]/);
    let [h, min] = timePart.split(':').map(Number);
    if (meridiem === 'PM' && h < 12) h += 12;
    if (meridiem === 'AM' && h === 12) h = 0;

    return new Date(
      parseInt(yyyy),
      parseInt(mm) - 1,
      parseInt(dd),
      h,
      min
    ).getTime();
  };

  sortAlertsByLatest = (alerts) => {
    return [...alerts].sort(
      (a, b) =>
        this.parseAlertTimestamp(b.timestamp) -
        this.parseAlertTimestamp(a.timestamp)
    );
  };

  // Poll fallback so ANY alert (from any backend path, not just the
  // WebSocket-pushed ones) shows up without a page reload.
  ALERT_POLL_MS = 30000;

  componentDidMount() {
    this.props.getTableSetting({});

    this.refreshAlerts();

    this.setState({ alerts: this.sortAlertsByLatest(this.props.alertEntries) });

    this.alertPoll = setInterval(this.refreshAlerts, this.ALERT_POLL_MS);
  }

  componentWillUnmount() {
    if (this.alertPoll) clearInterval(this.alertPoll);
  }

  refreshAlerts = () => {
    const locationId = this.props.userInfo?.selectedLocationId;
    if (locationId) this.props.fetchAlerts({ locationId });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevProps.alertEntries !== this.props.alertEntries ||
      prevProps.account !== this.props.account
    ) {
      this.removeSnoozedAlerts();
    }
  }

  removeSnoozedAlerts = () => {
    // account is the userTableSetting blob — undefined until
    // getTableSetting resolves, OR when the user has never saved one.
    // Optional-chain the whole path so a re-fetch (e.g. after delete)
    // doesn't crash before account hydrates.
    const snoozedAlertIds = (this.props.account?.snoozedAlerts || []).map(
      (alert) => alert.alertId
    );
    const unsnoozedAlerts = (this.props.alertEntries || []).filter(
      (alert) => !snoozedAlertIds.includes(alert.alertId)
    );

    this.setState({ alerts: this.sortAlertsByLatest(unsnoozedAlerts) });
  };

  toggleAlertDrawer = () => {
    this.setState({
      alertDrawerOpen: true,
    });
  };

  // "No clear subject" alert clicked — close the drawer and jump to Points
  // Watching (By session) for that date, with the giving employee filtered
  // and the "Unclear photos" chip pre-selected so the flagged entry is in view.
  navigateToPointWatching = (identifier) => {
    if (!identifier || !identifier.date) return;
    this.handleAlertDrawerClose();
    this.props.history.push('/point-watching', {
      pwDate: identifier.date,
      pwUserId: identifier.userId,
      pwUnclear: true,
    });
  };
  handleResolveAlert = (alertId, index) => {
    let data = {
      alertId: alertId,
      shippingTemplateChangedIndex: index,
      locationId: this.props.userInfo.selectedLocationId,
    };
    this.props.resolveAlert(data);
  };

  render() {
    return (
      <>
        <Tooltip title="Notifications">
          <IconButton
            style={{ marginRight: '4px' }}
            aria-label="alerts"
            onClick={this.toggleAlertDrawer}
          >
            <StyledBadge
              badgeContent={this.state.alerts.length}
              color="error"
              overlap="rectangular"
            >
              <NotificationsActiveIcon
                color="secondary"
                style={{ fontSize: '30px' }}
              />
            </StyledBadge>
          </IconButton>
        </Tooltip>

        <AlertsDrawer
          alertDrawerOpen={this.state.alertDrawerOpen}
          handleAlertDrawerClose={this.handleAlertDrawerClose}
          alertEntries={this.state.alerts}
          noAlerts={this.state.alerts.length === 0}
          closeDrawer={this.handleAlertDrawerClose}
          snoozeAlert={this.props.snoozeAlert}
          resolveAlert={this.handleResolveAlert}
          removeSnoozedAlerts={this.removeSnoozedAlerts}
          user={this.props.userInfo}
          deleteAllAlert={this.props.deleteAllAlerts}
          onAlertNavigate={this.navigateToPointWatching}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  alertEntries: alertEntriesSelector(state),
  userInfo: userInfoSelector(state),
  account: tableSettingSelector(state),
});

const actionCreators = {
  fetchAlerts,
  snoozeAlert,
  resolveAlert,
  getTableSetting,
  deleteAllAlerts,
};

Alerts.propTypes = {
  alertEntries: PropTypes.array,
  userInfo: PropTypes.object,
  account: PropTypes.object,
};

Alerts.defaultProps = {
  alertEntries: [],
  userInfo: {},
  account: {},
};

export default withRouter(
  withShipment(
    {
      mapStateToProps,
      actionCreators,
    },
    Alerts
  )
);
